import { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography, type TextFieldProps } from '@mui/material';
import { useDebounce } from '@/hooks/useDebounce';

/** A branch from the RBI bank master, as the IFSC suggestions show it. */
export interface IfscOption { ifsc: string; bank: string; branch?: string | null; address?: string | null; city?: string | null; state?: string | null }

/**
 * IFSC with suggestions from the RBI bank master: SBIN lists State Bank of India branches, SBIN0001 narrows them. Each
 * suggestion shows the IFSC, branch and city; picking one hands the branch to `onPick` so Bank and Branch fill in.
 * Typing is still free: an IFSC that is not suggested can be typed in full.
 */
export default function IfscField({ value, onChange, onPick, search, label, disabled, error, helperText, required, size = 'small' }: {
  value: string;
  onChange: (code: string) => void;
  onPick: (branch: IfscOption) => void;
  search: (typed: string) => Promise<IfscOption[]>;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: TextFieldProps['helperText'];
  required?: boolean;
  size?: 'small' | 'medium';
}) {
  const typed = useDebounce(value, 250);
  const [options, setOptions] = useState<IfscOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Suggestions start at the 4-letter bank prefix; a complete IFSC needs none.
    if (!/^[A-Z]{4}[A-Z0-9]{0,6}$/.test(typed)) { setOptions([]); return; }
    let live = true;
    setLoading(true);
    search(typed)
      .then((rows) => { if (live) setOptions(rows); })
      .catch(() => { if (live) setOptions([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [typed, search]);

  return (
    <Autocomplete<IfscOption, false, false, true>
      freeSolo
      disabled={disabled}
      open={open && options.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.ifsc)}
      isOptionEqualToValue={(a, b) => a.ifsc === b.ifsc}
      inputValue={value}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') onChange(v.toUpperCase().replace(/\s/g, '').slice(0, 11)); }}
      value={null}
      onChange={(_, v) => {
        if (v && typeof v !== 'string') { onChange(v.ifsc); onPick(v); setOpen(false); }
      }}
      renderOption={({ key, ...props }, o) => (
        <Box component="li" key={key} {...props} sx={{ display: 'block !important', py: '6px !important' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{o.ifsc}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {[o.branch, o.city].filter(Boolean).join(' · ')}{o.bank ? ` — ${o.bank}` : ''}
          </Typography>
        </Box>
      )}
      renderInput={(p) => (
        <TextField {...p} fullWidth size={size} label={label} required={required} error={error} helperText={helperText}
          slotProps={{
            htmlInput: { ...p.inputProps, maxLength: 11 },
            input: { ...p.InputProps, endAdornment: <>{loading ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</> },
          }} />
      )}
    />
  );
}
